import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { tokenStore } from './token'

/**
 * Same-origin base — browser never talks to the API host directly (no CORS).
 * Vite (dev) / edge (prod) reverse-proxy `/api/*` → real upstream
 * (localhost → 127.0.0.1:8000, else → https://{slug}.apidomain.com).
 *
 * Example: POST /api/client/auth/login → upstream POST /client/auth/login
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const API_KEY = import.meta.env.VITE_API_KEY || ''

export interface ApiErrorBody {
  message?: string
  code?: string
  details?: unknown
  errors?: Record<string, string[]>
}

export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  readonly details?: unknown
  readonly errors?: Record<string, string[]>

  constructor(status: number, body?: ApiErrorBody) {
    super(body?.message || `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = body?.code
    this.details = body?.details
    this.errors = body?.errors
  }

  /** First validation message for a field, if present. */
  fieldError(field: string): string | undefined {
    return this.errors?.[field]?.[0]
  }
}

/** Subscribers notified on hard 401 (invalid / expired Sanctum token). */
type UnauthorizedHandler = () => void
const unauthorizedHandlers = new Set<UnauthorizedHandler>()

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler)
  return () => unauthorizedHandlers.delete(handler)
}

function emitUnauthorized(): void {
  unauthorizedHandlers.forEach((h) => h())
}

function logRequest(config: InternalAxiosRequestConfig): void {
  if (!import.meta.env.DEV) return
  const method = (config.method || 'get').toUpperCase()
  console.debug(`[api] → ${method} ${config.baseURL ?? ''}${config.url ?? ''}`, {
    params: config.params,
  })
}

function logResponse(response: AxiosResponse): void {
  if (!import.meta.env.DEV) return
  console.debug(
    `[api] ← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
  )
}

function logError(error: AxiosError): void {
  if (!import.meta.env.DEV) return
  console.warn('[api] ✕', {
    status: error.response?.status,
    url: error.config?.url,
    message: error.message,
  })
}

/**
 * Centralized Axios client for the Laravel `/client/*` API.
 *
 * - Browser base always `/api` → Vite/edge reverse-proxy (CORS-safe)
 * - Proxied paths: `/client/auth/...`, `/client/positions/...`, `/client/settings`
 * - Auth: `Authorization: Bearer {sanctum plain-text token}` only (no refresh)
 * - Optional `x-api-key` when `VITE_API_KEY` is set (edge / multi-tenant gate)
 * - 401 on protected routes → clear session via `onUnauthorized`
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (API_KEY) {
    config.headers['x-api-key'] = API_KEY
  }

  const token = tokenStore.getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  logRequest(config)
  return config
})

apiClient.interceptors.response.use(
  (response) => {
    logResponse(response)
    return response
  },
  (error: AxiosError<ApiErrorBody>) => {
    logError(error)

    const status = error.response?.status
    const url = error.config?.url ?? ''

    // Public auth endpoints return 401/422 without forcing logout
    const isPublicAuth =
      url.includes('/client/auth/login') ||
      url.includes('/client/auth/register')

    if (status === 401 && !isPublicAuth) {
      tokenStore.clear()
      emitUnauthorized()
    }

    throw new ApiError(status ?? 0, error.response?.data)
  },
)

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<T>(url, { params }).then((r) => r.data),

  post: <T>(url: string, body?: unknown) =>
    apiClient.post<T>(url, body).then((r) => r.data),

  put: <T>(url: string, body?: unknown) =>
    apiClient.put<T>(url, body).then((r) => r.data),

  patch: <T>(url: string, body?: unknown) =>
    apiClient.patch<T>(url, body).then((r) => r.data),

  delete: <T>(url: string) => apiClient.delete<T>(url).then((r) => r.data),
}
